import { Field, FieldRow } from "@/components/settings/settings-kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NO_TARGET, useMetaTargets, type MetaTarget } from "@/hooks/useMetaTargets";
export { NO_TARGET, type MetaTarget } from "@/hooks/useMetaTargets";

const NEW = "__new__";

/**
 * "Run it in…": an existing campaign, optionally one of its ad sets, or a
 * new campaign (the default). Hidden entirely when Meta is not ready, so
 * the form it sits in stays usable without a connection.
 */
export function MetaTargetPicker({ value, onChange, idPrefix }: { value: MetaTarget; onChange: (t: MetaTarget) => void; idPrefix: string }) {
  const t = useMetaTargets(value.campaignId);
  if (!t.ready) return null;
  const pickCampaign = (v: string) => {
    if (v === NEW) return onChange(NO_TARGET);
    const c = t.campaigns.find((x) => x.id === v);
    onChange({ campaignId: v, campaignName: c?.name ?? null, adsetId: null, adsetName: null });
  };
  const pickAdset = (v: string) => {
    if (v === NEW) return onChange({ ...value, adsetId: null, adsetName: null });
    const a = t.adsets.find((x) => x.id === v);
    onChange({ ...value, adsetId: v, adsetName: a?.name ?? null });
  };
  return (
    <FieldRow>
      <Field label="Campaign" htmlFor={`${idPrefix}-campaign`} help="Where the ad runs; a new campaign gets its own budget and audience">
        <Select value={value.campaignId ?? NEW} onValueChange={(v) => v && pickCampaign(v)}>
          <SelectTrigger id={`${idPrefix}-campaign`}>
            <SelectValue placeholder={t.campaignsLoading ? "Loading campaigns" : "New campaign"} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={NEW}>New campaign</SelectItem>
            {t.campaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}{c.status && c.status !== "ACTIVE" ? ` (${c.status.toLowerCase()})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Ad set" htmlFor={`${idPrefix}-adset`} help={value.campaignId ? "Reuse an ad set's budget and audience, or add a new one" : "Choose a campaign first"}>
        <Select value={value.adsetId ?? NEW} onValueChange={(v) => v && pickAdset(v)} disabled={!value.campaignId}>
          <SelectTrigger id={`${idPrefix}-adset`}>
            <SelectValue placeholder={t.adsetsLoading ? "Loading ad sets" : "New ad set"} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={NEW}>New ad set</SelectItem>
            {t.adsets.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}{a.status && a.status !== "ACTIVE" ? ` (${a.status.toLowerCase()})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </FieldRow>
  );
}
